import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input } from 'formik-antd';
import { Spin, Row, Col } from 'antd';
import { Formik, FormikHelpers } from 'formik';
import queryString from 'query-string';
import * as Yup from 'yup';
import cx from 'classnames';

import {
  resetPassword,
  checkIsResetPasswordLinkExpired,
} from 'redux/reducers/authenticationSlice';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import SubmitButton from 'components/formik/SubmitButton';
import FormItem from 'components/formik/FormItem';
import { PasswordValidationRules } from 'utils/authUtils';
import PasswordValidationRuleBox from '../PasswordValidationRuleBox';
import { useAppDispatch } from 'hooks/useAppDispatch';

import logo from 'assets/img/logo-dark.svg';

import style from '../AuthorizationPage.module.less';

interface FormValues {
  password: string;
}

const requiredMessage = 'Field is required';

const validationSchema = Yup.object({
  password: Yup.string()
    .required(requiredMessage)
    .matches(PasswordValidationRules.MinimumCharacters)
    .matches(PasswordValidationRules.MaximumCharacters)
    .matches(PasswordValidationRules.LowercaseCharacter)
    .matches(PasswordValidationRules.SpecialCharacter)
    .matches(PasswordValidationRules.UppercaseCharacter)
    .matches(PasswordValidationRules.OneNumber),
});

export default function ResetPasswordPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { uid: uId, c: code } = queryString.parse(location.search);
  const isUrlValid = !!uId && !!code;

  const checkIsLinkExpired = useCallback(async () => {
    if (!isUrlValid) return;

    setIsLoading(true);

    const isLinkExpired = await dispatch(
      checkIsResetPasswordLinkExpired({ uId, code }),
    );

    if (checkIsResetPasswordLinkExpired.fulfilled.match(isLinkExpired)) {
      if (!!isLinkExpired.payload) {
        navigate('/login');
        BaseAlert.error('Link expired. Please re-submit new request.');
      }

      setIsLoading(false);
    } else {
      BaseAlert.error(isLinkExpired.error.message || ApiErrorMessage);
    }
  }, [uId, code, dispatch, navigate, isUrlValid]);

  useEffect(() => {
    checkIsLinkExpired();
  }, [checkIsLinkExpired]);

  const handleSubmit = async (
    values: FormValues,
    formikHelpers: FormikHelpers<FormValues>,
  ) => {
    const result = await dispatch(
      resetPassword({
        code,
        uId,
        password: values.password,
      }),
    );

    if (resetPassword.fulfilled.match(result)) {
      navigate('/login', { replace: true });
      BaseAlert.success(
        'Your password has been changed. Please log in to continue.',
      );
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  if (isLoading) return <Spin size="large" />;

  if (!isUrlValid) {
    return <h3 className="no-data-msg">Incorrect URL</h3>;
  }

  return (
    <div className={cx('scroll-box', style.authPage)}>
      <Row justify="end" className={style.fullHeight}>
        <Col xl={14}>
          <div className={style.authPageBg} />
        </Col>
        <Col xs={24} xl={10}>
          <Formik
            initialValues={{ password: '' }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(formik) => (
              <Form className={style.authPageForm} layout="vertical">
                <div className={style.authPageTitle}>
                  <div>
                    Create a new password
                    <div className={cx(style.authPageLogo, style.pageLogo)}>
                      <img src={logo} alt="myBasePay" />
                    </div>
                  </div>
                </div>
                <div className={style.authPageContent}>
                  <FormItem
                    data-id="resetPasswordField"
                    name="password"
                    showValidateError={
                      formik.errors.password === requiredMessage
                    }
                    label="New password"
                  >
                    <Input.Password
                      size="large"
                      name="password"
                      type="password"
                      autoComplete="off"
                    />
                  </FormItem>
                  <PasswordValidationRuleBox
                    password={formik.values.password}
                  />

                  <Row gutter={20} align="middle" className={style.submitBox}>
                    <Col xs={24} md={10}>
                      <SubmitButton
                        data-id="confirmPasswordBtn"
                        type="primary"
                        size="large"
                        block
                      >
                        Confirm
                      </SubmitButton>
                    </Col>
                  </Row>
                </div>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
}
